import React, { useState } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import { BRANCHES } from "../data/branches";
import { IMG } from "../data/site";
import { MapPin, Search } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Branches = () => {
  const { t, lang } = useLang();
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  const copy = {
    pilgrimage: lang === "kn" ? "ನಿಮ್ಮ ತೀರ್ಥಯಾತ್ರೆಯನ್ನು ಯೋಜಿಸಿ" : "Plan your pilgrimage",
    nearestBranch: lang === "kn" ? "ನಿಮ್ಮ ಹತ್ತಿರದ ಶಾಖಾ ಮಠವನ್ನು ಸಂಪರ್ಕಿಸಿ" : "Connect with your nearest branch Matha",
    contact: lang === "kn" ? "ಸಂಪರ್ಕಿಸಿ" : "Contact us",
    festivals: lang === "kn" ? "ಉತ್ಸವಗಳು" : "Festivals",
  };
  const [q, setQ] = useState("");
  const [state, setState] = useState("All");
  const states = ["All", ...Array.from(new Set(BRANCHES.map((b) => b.state)))];
  const filtered = BRANCHES.filter((b) => (state === "All" || b.state === state) && (q === "" || `${b.name} ${b.location}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("branches.hero.eyebrow")}
        title={t("branches.hero.title")}
        subtitle={t("branches.hero.subtitle")}
        image={IMG.chariot}
      />

      {/* Filters */}
      <section className="py-14" data-testid="branches-filter">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <div className="flex-1 flex items-center gap-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-full px-5 py-3">
              <Search className="w-4 h-4 text-[#B65A14]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "kn" ? "ಹೆಸರು ಅಥವಾ ಸ್ಥಳದಿಂದ ಶಾಖೆಗಳನ್ನು ಹುಡುಕಿ…" : "Search branches by name or location…"}
                className="bg-transparent outline-none flex-1 text-sm text-[#3B2A1A] placeholder:text-[#3B2A1A]/50"
                data-testid="branch-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {states.map((s) => (
                <button
                  key={s}
                  onClick={() => setState(s)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border ${state === s ? "bg-[#B65A14] text-[#FBF7F0] border-[#B65A14]" : "bg-[#FBF7F0] text-[#3B2A1A] border-[#D4AF37]/30 hover:border-[#D4AF37]"}`}
                  data-testid={`state-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {s === "All" ? (lang === "kn" ? "ಎಲ್ಲಾ" : "All") : (lang === "kn" ? (s === "Karnataka" ? "ಕರ್ನಾಟಕ" : s === "Tamil Nadu" ? "ತಮಿಳುನಾಡು" : s === "Kerala" ? "ಕೇರಳ" : s) : s)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Branch grid */}
      <section className="pb-24" data-testid="branches-grid">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filtered.map((b) => (
            <div key={b.name} className="bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl overflow-hidden card-lift flex flex-col" data-testid={`branch-${b.name.replace(/\W/g, "-").toLowerCase()}`}>
              {b.image && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#F2E7D5] text-[#B65A14] flex items-center justify-center"><MapPin className="w-4 h-4" /></div>
                  <div className="overline text-[#B65A14]">{pick(b, "state")}</div>
                </div>
                <h3 className="font-serif-display text-xl text-[#3B2A1A] leading-tight">{pick(b, "name")}</h3>
                <div className="text-sm text-[#B65A14] mt-1">{pick(b, "location")}</div>
                <div className="mt-4 gold-underline" />
                <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed flex-1">{pick(b, "desc")}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider variant="lotus" />

      <CTASection
        eyebrow={copy.pilgrimage}
        title={copy.nearestBranch}
        primary={{ label: copy.contact, to: "/contact" }}
        secondary={{ label: copy.festivals, to: "/events" }}
        image={IMG.processional}
      />
    </PageLayout>
  );
};

export default Branches;
